import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UTRModal from '../../Modal';
import SavedListModalActions from '../SavedListModalActions';
import useListNameForm from '../useListNameForm';
import { createSavedPlayerList } from '../../../actions/saved-lists-actions';
import { PopupTypes } from '../../../utils/constants';
import { MAX_LIST_NAME_LENGTH } from '../listErrors';

import styles from './createListModal.scss';

// The ticket's data-testid contract pins this exact literal (no `modal.` namespace) — the web
// verifier's Playwright selectors depend on it. Intentional deviation from CLAUDE.md rule 4,
// matching the sibling SaveToListModal; don't "fix" it to the general convention.
const TESTID_PREFIX = 'create-list-modal';
const NAME_ERROR_ID = `${TESTID_PREFIX}-name-error`;
const MODAL_TITLE = 'Create New List';
// Figma's primary button reads "CREATE" (uppercased from "Create" by SavedListModalActions' CSS);
// without this the shared footer would default to "Save".
const CREATE_LABEL = 'Create';
const NAME_LABEL = 'List Name';
const SUCCESS_TOAST_TITLE = 'List Successfully Created';
const SUCCESS_TOAST_MESSAGE = 'Your new list has been added to Saved Lists.';
const VIEW_LIST_LABEL = 'View List';
const GENERIC_INLINE_ERROR_MESSAGE = 'We couldn\'t create this list. Please try again.';
const GENERIC_ERROR_TOAST_MESSAGE = 'We couldn\'t create your list. Please try again.';

// The success toast's "View List" action has nowhere to navigate: List Detail (Story 17) is
// container state inside SavedListsTabContent, not a route, so a freshly created list can't be
// deep-linked to from a global toast. Documented no-op until a route exists — same posture as TMS-2146.
const handleViewListClick = () => {};

const CreateListModal = ({ onCloseClick }) => {
    const dispatch = useDispatch();
    const onSuccess = useSelector((state) => state.popups[`${PopupTypes.CREATE_LIST_POPUP}OnSuccess`]);

    const { name, nameError, isSaving, isSaveEnabled, handleNameChange, handleSave, handleKeyDown } = useListNameForm({
        onCloseClick,
        onSubmit: (listName) => dispatch(createSavedPlayerList(listName)),
        onSubmitted: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        buildSuccessToast: () => ({
            title: SUCCESS_TOAST_TITLE,
            message: SUCCESS_TOAST_MESSAGE,
            actionLabel: VIEW_LIST_LABEL,
            onAction: handleViewListClick,
        }),
        genericInlineMessage: GENERIC_INLINE_ERROR_MESSAGE,
        genericToastMessage: GENERIC_ERROR_TOAST_MESSAGE,
    });

    return (
        <UTRModal
            title={MODAL_TITLE}
            onCloseClick={onCloseClick}
            className={styles.createListModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={`${TESTID_PREFIX}.desktop-title`}>
                {MODAL_TITLE}
            </div>
            <label
                className={styles.fieldLabel}
                htmlFor={`${TESTID_PREFIX}-name-input`}
                data-testid={`${TESTID_PREFIX}.name-label`}
            >
                {NAME_LABEL}
            </label>
            {/*
              Native input, not the shared Form/Input component: the ticket's data-testid contract
              requires the exact literal id below (the web verifier's Playwright selectors depend on
              it), and Form/Input's controlled mode always suffixes its rendered input/error with
              `.field`/`.error`, which would break an exact-id, real-click-driven contract. Same
              rationale as the sibling SaveToListModal.
            */}
            <input
                id={`${TESTID_PREFIX}-name-input`}
                type="text"
                className={`${styles.nameInput} ${nameError ? styles.nameInputError : ''}`}
                value={name}
                maxLength={MAX_LIST_NAME_LENGTH}
                onChange={handleNameChange}
                onKeyDown={handleKeyDown}
                aria-invalid={!!nameError}
                aria-describedby={nameError ? NAME_ERROR_ID : undefined}
                data-testid={`${TESTID_PREFIX}.name-input`}
            />
            <div className={styles.metaRow}>
                {nameError && (
                    <div
                        id={NAME_ERROR_ID}
                        role="alert"
                        className={styles.nameError}
                        data-testid={`${TESTID_PREFIX}.name-error`}
                    >
                        {nameError}
                    </div>
                )}
                <div className={styles.charCounter} data-testid={`${TESTID_PREFIX}.char-counter`}>
                    {`${name.length}/${MAX_LIST_NAME_LENGTH}`}
                </div>
            </div>
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCloseClick}
                onSave={handleSave}
                saveDisabled={!isSaveEnabled || isSaving}
                saveLabel={CREATE_LABEL}
                pinned
            />
        </UTRModal>
    );
};

CreateListModal.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
};

export default CreateListModal;
